import React, {Component} from "react";
import {Link} from "react-router";
import TopNav from "../../../common/nav/TopNav";
import Footer from "../../../common/Footer";
import PageBlock from "../../../common/PageBlock";
import CodeBlock from "../../../common/CodeBlock";
import {currentSiteName} from "../../../service/SiteService";

class Actions extends Component {
    render() {
        return (
            <div>
                <TopNav tab="docs"/>

                <PageBlock>
                    <div className="container">

                        <div className="row">
                            <div className="col-lg-12">
                                <h1 className="page-header">About Actions</h1>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12">
                                <p>
                                    Control how your bot responds to your messages.
                                    You can add actions to trigger your jobs and any parameters they require, configured using your <Link
                                    to="/bot/actions">Actions</Link> configuration.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12">
                                <h3>Action types</h3>
                                <p>
                                    {currentSiteName} has both built-in actions and custom actions. Examples of built in actions are the lock/unlock actions. Custom
                                    actions are triggers for your Rundeck/Jenkins jobs.
                                </p>

                                <h3>Built-in actions</h3>

                                <p>There are a number of built in actions, such as:</p>

                                <ul>
                                    <li><code>@cloudMocks help</code> - show usage information.</li>
                                    <li><code>@cloudMocks lock [action or tag name]</code> - lock action(s) to prevent them being triggered accidentally.</li>
                                    <li><code>@cloudMocks lock [option name] [option value]</code> - lock an option with a given value.</li>
                                    <li><code>@cloudMocks unlock [action or tag name]</code> - unlock locked action(s).</li>
                                    <li><code>@cloudMocks unlock [option name] [option value]</code> - lock an option with a given value.</li>
                                    <li><code>@cloudMocks status [action or tag name]</code> - show status of action(s).</li>
                                    <li><code>@cloudMocks enable [action or tag name]</code> - set the Rundeck execution status for a job - <em>Note:</em> this
                                        requires the Rundeck ACL to permit the API user to set the execution status of a job.
                                    </li>
                                    <li><code>@cloudMocks disable [action or tag name]</code> - set the Rundeck execution status for a job - <em>Note:</em> this
                                        requires the Rundeck ACL to permit the API user to set the execution status of a job.
                                    </li>
                                </ul>

                                <h3>Custom actions</h3>
                                <p>Here's an example Action configuration:</p>
                                <CodeBlock code={`version: '1'
actions:
  services:
    jobId: 9374f1c8-7b3f-4145-8556-6b55551fb60f
    template: deploy services {version} to {environment}
`}/>
                                <div className="panel panel-info">
                                    <div className="panel-heading"><h3 className="panel-title">Structure</h3></div>
                                    <div className="panel-body">
                                        <ul>
                                            <li>All files must specify version ‘1’</li>
                                            <li>All actions must sit under a top level <code>actions</code> block</li>
                                            <li>Each action must have a name (it’s <code>services</code> in this example)</li>
                                            <li>Each action must have either:

                                                <ul>
                                                    <li>a Rundeck job ID (obtain this from Rundeck), or</li>
                                                    <li>a Jenkins job name</li>
                                                </ul>
                                            </li>
                                            <li>Each action must have a template - more details below</li>
                                            <li>Each action may optionally specify a list of tags</li>
                                            <li>Each action may optionally specify option configuration, such as:

                                                <ul>
                                                    <li>static values</li>
                                                    <li>value transformers</li>
                                                    <li>whether the option can be locked</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <blockquote>
                                    <p>Tip: Check out the <Link to="/docs/bot/samples">Samples</Link> page for sample configuration files.</p>
                                </blockquote>

                                <h4>Drivers</h4>
                                <p>Actions should specify a driver. The available drivers are:</p>

                                <ul>
                                    <li>rundeck</li>
                                    <li>jenkins</li>
                                </ul>

                                <blockquote>
                                    <p>Note: if none is specified, the driver is assumed to be <code>rundeck</code>.</p>
                                </blockquote>

                                <p><em>Example:</em></p>

                                <CodeBlock code={`version: '1'
actions:
  services:
    driver: jenkins
    jobId: my-jenkins-job
    template: deploy web {version} to {environment}
`}/>

                                <p><strong>Important:</strong> Ensure that you set the variables corresponding to the driver(s) you use, such as the base URL,
                                    API key/username etc.</p>

                                <h4>Action template</h4>

                                <p>An action template provides the syntax for invoking the command. </p>

                                <p><em>Example:</em></p>

                                <pre><code>deploy services</code></pre>

                                <p>A template also allows you to specify job options as placeholders, such as:</p>

                                <CodeBlock code={`deploy services {version} to {environment}`}/>

                                <p>In this example both <em>version</em> and <em>environment</em> are captured from the command, such as:</p>

                                <pre><code>@cloudMocks deploy services 1.0 to UAT</code></pre>

                                <p>This will result in the action being fired, passing the following options:</p>

                                <ul>
                                    <li>version=1.0</li>
                                    <li>environment=UAT</li>
                                </ul>

                                <h4>Static option values</h4>

                                <p>You might want to pass an option value to a job every time, and not require the user to provide it. You can accomplish this
                                    using the <code>value</code> property of an option within the <code>options</code> action block:</p>

                                <CodeBlock code={`version: '1'
actions:
  services:
    jobId: 9374f1c8-7b3f-4145-8556-6b55551fb60f
    template: deploy services {version} to {environment}
    options:
      myOption:
        value: someValue
      myOtherOption:
        value: someOtherValue
`}/>

                                <p>This will result in the action being fired, passing the following options:</p>

                                <ul>
                                    <li>version=1.0</li>
                                    <li>environment=UAT</li>
                                    <li><em>myOption=someValue</em></li>
                                    <li><em>myOtherOption=someOtherValue</em></li>
                                </ul>

                                <h4>Transforming options</h4>

                                <p>You might want to transform an option value provided by a user before it is passed to a job. You can accomplish this using
                                    the <code>transformers</code> section of an option within the <code>options</code> action block:</p>

                                <CodeBlock code={`version: '1'
actions:
  services:
    jobId: 9374f1c8-7b3f-4145-8556-6b55551fb60f
    template: deploy services {version} to {environment}
    options:
      version:
        transformers:
          - LOWERCASE
      environment:
        transformers:
          - UPPERCASE
`}/>

                                <p>If the user typed this command:</p>

                                <pre><code>@cloudMocks deploy services V1.0 to uat</code></pre>

                                <p>This will result in the action being fired, passing the following options:</p>

                                <ul>
                                    <li>version=v1.0 (note: lowercased)</li>
                                    <li>environment=UAT (note: uppercased)</li>
                                </ul>

                                <h4>Tags and multiple job actions</h4>

                                <p>Sometimes actions can be run on multiple jobs. To do this, set the <code>tags</code> block:</p>

                                <CodeBlock code={`version: '1'
actions:
  deploy-services:
    jobId: 9374f1c8-7b3f-4145-8556-6b55551fb60f
    template: deploy services {version} to {environment}
    tags:
      - services

  restart-services:
    jobId: e9d12eec-abff-4780-89cd-56a48b8c67be
    template: restart services in {environment}
    tags:
      - services
`}/>

                                <p>Here, two actions are defined: <code>deploy-services</code> and <code>restart-services</code>, both tagged with <code>services</code>.
                                    This means you can do things like:</p>

                                <pre><code>@cloudMocks lock services
</code></pre>

                                <p>…and both actions will be locked.</p>

                                <blockquote>
                                    <p>Tip: There is a special tag set on all actions, named 'all'. This means you can do things like <code>@cloudMocks lock
                                        all</code>.</p>
                                </blockquote>

                                <h3>Locking and Security</h3>

                                <p>You can also lock and unlock jobs. Control who has access to performs certain actions
                                    and on which jobs.</p>

                                <p>To learn more, see the <Link to="/docs/bot/action-security">Action Security</Link> section.</p>
                            </div>
                        </div>

                    </div>
                </PageBlock>

                <Footer />
            </div>
        );
    }
}

export default Actions;



// WEBPACK FOOTER //
// ./src/modules/views/docs/bot/Actions.js